import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { EyeIcon, XMarkIcon } from '@heroicons/react/24/solid';
import ModalPortal from './ModalPortal';
import Footer from './common/Footer';

// Set the worker source using the public URL
const pdfjsWorkerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerSrc;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Dashboard = () => {
  // State variables
  const [results, setResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('company');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState('');
  const [viewedItems, setViewedItems] = useState(() => {
    const saved = localStorage.getItem('viewedItems');
    return saved ? JSON.parse(saved) : [];
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('role') === 'admin';

  // Handle main search functionality - defined before use
  const handleSearch = React.useCallback(async () => {
    setError('');
    setResults([]);
    setProductSuggestions([]); // Clear suggestions when searching
    
    if (!searchQuery) return;

    try {
      if (searchType === 'company') {
        const response = await axios.get(
          `${API_BASE_URL}/api/search/military_companies?query=${searchQuery}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        setResults(response.data);
        localStorage.setItem('lastSearchResults', JSON.stringify({
          results: response.data,
          query: searchQuery,
          type: searchType
        }));
      } else if (searchType === 'product') {
        const endpoint = selectedProduct 
          ? `${API_BASE_URL}/api/search/products/${selectedProduct.id}/companies`
          : `${API_BASE_URL}/api/search/product?query=${searchQuery}`;
          
        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setResults(response.data);
        localStorage.setItem('lastSearchResults', JSON.stringify({
          results: response.data,
          query: searchQuery,
          type: searchType
        }));
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Error performing search. Please try again.');
    }
  }, [searchQuery, searchType, selectedProduct]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      const delayDebounceFn = setTimeout(() => {
        if (searchType === 'company') {
          handleSearch();
        }
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchQuery, searchType, handleSearch]);

  // Add with other useEffect hooks
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Add after other useEffect hooks
  useEffect(() => {
    const savedSearch = localStorage.getItem('lastSearchResults');
    if (savedSearch) {
      const { results: savedResults, query, type } = JSON.parse(savedSearch);
      setResults(savedResults);
      setSearchQuery(query);
      setSearchType(type);
    }
  }, []);

  useEffect(() => {
    // Check subscription status
    const checkSubscription = () => {
      const subscription_date = localStorage.getItem('subscription_date');
      if (subscription_date) {
        const currentDate = new Date();
        const subscriptionDate = new Date(subscription_date);
        if (subscriptionDate < currentDate) {
          setIsSubscriptionExpired(true);
          setError('Your subscription has expired. Please contact the administrator.');
          localStorage.setItem('subscriptionExpired', 'true');
        }
      }
    };

    checkSubscription();
  }, []);

  // Handle product search for autocomplete
  const handleProductSearch = async (query) => {
    if (!query || query.length < 2) {
      setProductSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/search/products/autocomplete?query=${query}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setProductSuggestions(response.data);
    } catch (err) {
      console.error('Product search error:', err);
      setError('Error searching products. Please try again.');
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchQuery(product.name);
    setProductSuggestions([]);
    handleSearch();
  };

  // Handle search input changes
  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchType === 'product') {
      handleProductSearch(value);
    }
    
    if (!value) {
      setResults([]);
      setProductSuggestions([]);
      setSelectedProduct(null);
      setViewedItems([]);
      localStorage.removeItem('viewedItems');
      localStorage.removeItem('lastSearchResults');
    }
  };

  // Handle search type changes
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchQuery('');
    setResults([]);
    setProductSuggestions([]);
    setSelectedProduct(null);
    setError('');
  };

  // Pagination logic
  const resultsPerPage = 25;
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = results.slice(indexOfFirstResult, indexOfLastResult);
  const totalPages = Math.ceil(results.length / resultsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // PDF handling functions
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setPdfError('');
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading document:', error);
    setPdfError('Error loading PDF. Please try again.');
    setIsLoading(false);
  };

  const handlePrint = () => {
    // Create an iframe to host the PDF for printing
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${API_BASE_URL}/pdfs/datas/${selectedPdf}#toolbar=0&view=FitH`;
    
    iframe.onload = () => {
      try {
        iframe.contentWindow.print();
        
        // Remove the iframe after printing (delayed to ensure print dialog shows)
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      } catch (err) {
        console.error('Print error:', err);
        // Fallback: open in new window if iframe printing fails
        window.open(`${API_BASE_URL}/pdfs/datas/${selectedPdf}`, '_blank');
      }
    };
    
    document.body.appendChild(iframe);
  };

  const handleDownload = () => {
    if (selectedPdf) {
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}/pdfs/datas/${selectedPdf}`;
      link.download = selectedPdf; // Use the PDF filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPdf(null);
    setNumPages(null);
    setPageNumber(1);
  };

  // Company click handler
  const handleCompanyClick = async (result) => {
    if (result.file_name) {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE_URL}/pdfs/datas/${result.file_name}`);
        if (!response.ok) throw new Error('PDF not found');
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/pdf')) {
          throw new Error('Invalid PDF file');
        }

        setSelectedPdf(result.file_name);
        setIsModalOpen(true);
        
        // Mark item as viewed
        const newViewedItems = [...viewedItems];
        if (!newViewedItems.includes(result.company_name)) {
          newViewedItems.push(result.company_name);
          setViewedItems(newViewedItems);
          localStorage.setItem('viewedItems', JSON.stringify(newViewedItems));
        }
      } catch (err) {
        console.error('PDF error:', err);
        setPdfError('Error loading PDF. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Text highlighting function
  const highlightText = (text, query) => {
    if (!query) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? 
        <span key={index} className="bg-yellow-500/30">{part}</span> : 
        part
    );
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  if (isSubscriptionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="bg-gray-800/90 shadow-lg rounded-lg p-8 border border-yellow-700 backdrop-blur-sm max-w-md w-full">
          <h2 className="text-2xl font-bold text-yellow-500 mb-4">Subscription Expired</h2>
          <p className="text-gray-300 mb-6">Your subscription has expired. Please contact the administrator to renew your subscription.</p>
          <button
            onClick={() => {
              localStorage.clear();
              navigate('/');
            }}
            className="w-full bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-600 transition-colors duration-200"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <nav className="bg-gray-800 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-yellow-500">{process.env.REACT_APP_NAME}</h1>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="text-gray-300 hover:text-yellow-500 transition-colors duration-200"
                >
                  Admin Dashboard
                </button>
              )}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/settings')}
                className="text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                Settings
              </button>
              <div className="w-px h-4 bg-gray-700"></div>
              <button
                onClick={handleLogout}
                className="text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow py-6 sm:py-8 lg:py-10 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl w-full mx-auto">
          <div className="bg-gray-800/90 shadow-lg rounded-lg p-6 sm:p-8 lg:p-10 border border-gray-700 backdrop-blur-sm">
            <div className="mb-8 sm:mb-10">
              <h2 className="text-2xl font-bold text-yellow-500 mb-3">Search By Product / Company</h2>
              <p className="text-gray-400">Search for defence equipment companies and their products</p>
            </div>
            <div className="space-y-6">
              <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
                <div className="w-full md:w-1/3">
                  <select
                    value={searchType}
                    onChange={handleSearchTypeChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-md text-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                  >
                    <option value="company">Search by Company</option>
                    <option value="product">Search by Product</option>
                  </select>
                </div>
                <div className="w-full md:w-2/3 relative">
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                  }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchQueryChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                      placeholder={`Enter ${searchType} name...`}
                      className="w-full px-4 py-3 pr-10 bg-gray-700 border border-gray-600 rounded-md text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors duration-200"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setResults([]);
                          setProductSuggestions([]);
                          setSelectedProduct(null);
                          setViewedItems([]);
                          localStorage.removeItem('viewedItems');
                          localStorage.removeItem('lastSearchResults');
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-yellow-500 focus:outline-none"
                      >
                        &#x2715;
                      </button>
                    )}
                    
                    {/* Product Suggestions Dropdown */}
                    {searchType === 'product' && productSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-lg">
                        <ul className="py-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
                          {productSuggestions.map((product) => (
                            <li
                              key={product.id}
                              onClick={() => handleProductSelect(product)}
                              className="px-4 py-2 hover:bg-gray-600 cursor-pointer text-gray-200 hover:text-yellow-500"
                            >
                              {product.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-6 p-4 bg-red-900/50 border border-red-700 rounded-md text-red-200 text-sm">
                {error}
              </div>
            )}

            {results.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-medium text-yellow-500 mb-4">Search Results</h2>
                <div className="space-y-4">
                  {currentResults.map((result, index) => (
                    <div
                      key={index}
                      className={`bg-gray-700 p-6 rounded-md border relative transition-all duration-200 ${
                        viewedItems.includes(result.company_name)
                          ? 'border-blue-500 bg-gray-700/90 shadow-[0_0_10px_rgba(59,130,246,0.2)]'
                          : 'border-gray-600 hover:border-yellow-500'
                      }`}
                    >
                      {viewedItems.includes(result.company_name) && (
                        <div className="absolute top-4 right-4 flex items-center space-x-2 bg-blue-500/20 px-2 py-1 rounded-full">
                          <EyeIcon className="h-4 w-4 text-blue-400" />
                          <span className="text-xs text-blue-400">Viewed</span>
                        </div>
                      )}
                      <h3 
                        className={`text-md font-medium cursor-pointer pr-20 ${
                          viewedItems.includes(result.company_name)
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-yellow-500 hover:text-yellow-400'
                        }`}
                        onClick={() => handleCompanyClick(result)}
                      >
                        {highlightText(result.company_name, searchQuery)}
                      </h3>
                      <p className="mt-2 text-sm text-gray-300">
                        {result.description}
                      </p>
                      {result.file_name && (
                        <p className={`mt-2 text-sm ${
                          viewedItems.includes(result.company_name)
                            ? 'text-blue-400'
                            : 'text-yellow-400'
                        }`}>
                          Click to view PDF
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {results.length > 25 && (
                  <div className="mt-6 flex justify-center items-center space-x-2">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => paginate(index + 1)}
                        className={`px-4 py-2 rounded-md transition-colors duration-200 
                          ${currentPage === index + 1 
                            ? 'bg-yellow-500 text-gray-900' 
                            : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* PDF Viewer Modal */}
            {isModalOpen && (
              <ModalPortal>
                <div 
                  className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[9999] print-hide"
                  onClick={closeModal}
                >
                  <div 
                    className="fixed inset-0 sm:inset-8 md:inset-16 bg-gray-800 rounded-lg shadow-2xl flex flex-col"
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Modal Header - hide when printing */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-700 print-hide">
                      <h2 className="text-lg font-medium text-yellow-500">PDF Viewer</h2>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={handlePrint}
                          className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M5 4V3c0-.55.45-1 1-1h8c.55 0 1 .45 1 1v1h1c1.66 0 3 1.34 3 3v5c0 1.66-1.34 3-3 3h-1v1c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1v-1H4c-1.66 0-3-1.34-3-3V7c0-1.66 1.34-3 3-3h1zm0 2H4c-.55 0-1 .45-1 1v5c0 .55.45 1 1 1h12c.55 0 1-.45 1-1V7c0-.55-.45-1-1-1h-1v3H5V6zm1-3v3h8V3H6z"/>
                          </svg>
                          Print
                        </button>
                        <button
                          onClick={handleDownload}
                          className="inline-flex items-center px-4 py-2 bg-yellow-500 text-gray-900 rounded-md hover:bg-yellow-600 transition-colors duration-200"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 2c.55 0 1 .45 1 1v7.59l2.29-2.29c.39-.39 1.02-.39 1.41 0 .39.39.39 1.02 0 1.41l-4 4c-.39.39-1.02.39-1.41 0l-4-4c-.39-.39-.39-1.02 0-1.41.39-.39 1.02-.39 1.41 0L9 10.59V3c0-.55.45-1 1-1zm-8 12c0-.55.45-1 1-1h14c.55 0 1 .45 1 1s-.45 1-1 1H3c-.55 0-1-.45-1-1z"/>
                          </svg>
                          Download
                        </button>
                        <button
                          onClick={closeModal}
                          className="p-2 hover:bg-gray-700 rounded-full transition-colors duration-200"
                          aria-label="Close modal"
                        >
                          <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-200" />
                        </button>
                      </div>
                    </div>

                    {/* Modal Content */}
                    <div className="flex-1 overflow-hidden">
                      <div className="h-full overflow-y-auto px-2 sm:px-4 py-6 pdf-viewer-container">
                        <style>
                          {`
                            .pdf-viewer-container {
                              -webkit-overflow-scrolling: touch;
                              overscroll-behavior: contain;
                            }
                            .pdf-container {
                              max-width: 100%;
                              margin: 0 auto;
                            }
                            .pdf-page {
                              touch-action: pan-y pinch-zoom;
                              max-width: 100%;
                              height: auto !important;
                            }
                            .pdf-page canvas {
                              max-width: 100% !important;
                              height: auto !important;
                            }
                            @media (max-width: 768px) {
                              .pdf-container {
                                padding: 0;
                              }
                            }
                          `}
                        </style>
                        {pdfError ? (
                          <div className="p-4 bg-red-900/50 border border-red-700 rounded-md text-red-200 text-sm print-hide">
                            {pdfError}
                          </div>
                        ) : isLoading ? (
                          <div className="flex items-center justify-center py-8 print-hide">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                            <span className="ml-3 text-yellow-500">Loading PDF...</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Document
                              file={`${API_BASE_URL}/pdfs/datas/${selectedPdf}`}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              className="pdf-document"
                              loading={
                                <div className="flex items-center justify-center py-8 print-hide">
                                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
                                  <span className="ml-3 text-yellow-500">Loading PDF...</span>
                                </div>
                              }
                            >
                              <div className="pdf-container touch-manipulation" style={{ width: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                <Page 
                                  pageNumber={pageNumber} 
                                  renderTextLayer={false}
                                  renderAnnotationLayer={false}
                                  width={Math.min(
                                    window.innerWidth < 768 ? window.innerWidth - 32 : window.innerWidth - 240, 
                                    800
                                  )}
                                  className="mx-auto pdf-page"
                                  scale={1}
                                />
                              </div>
                            </Document>

                            {numPages && (
                              <div className="flex justify-center items-center space-x-4 mt-6 sticky bottom-0 bg-gray-800 py-4 border-t border-gray-700 w-full print-hide">
                                <button
                                  onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                                  disabled={pageNumber <= 1}
                                  className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
                                >
                                  Previous
                                </button>
                                <span className="text-gray-300">
                                  Page {pageNumber} of {numPages}
                                </span>
                                <button
                                  onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                                  disabled={pageNumber >= numPages}
                                  className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </ModalPortal>
            )}
          </div>
        </div>
      </main>

      <footer className="flex-shrink-0 mt-auto">
        <Footer />
      </footer>
    </div>
  );
};

export default Dashboard;